import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { ZoomIn, Loader2 } from "lucide-react";
import { toast } from "sonner";

async function getCroppedBlob(src, crop) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, size, size);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.9));
}

export default function PhotoCropper({ src, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, pixels);
      await onCropped(blob);
    } catch (e) {
      toast.error("Couldn't process that photo. Please try another image.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="photo-cropper-dialog">
        <DialogHeader><DialogTitle className="font-display text-navy">Frame the ID Photo</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Drag to reposition and use the slider to zoom, then save the cropped photo.</DialogDescription>
        <div className="relative w-full h-64 bg-navy rounded-xl overflow-hidden" data-testid="cropper-area">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="rect"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <ZoomIn className="w-4 h-4 text-slate-400" />
          <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={(v) => setZoom(v[0])} className="flex-1" data-testid="cropper-zoom" />
        </div>
        <p className="text-xs text-slate-400">Drag to reposition · pinch or slide to zoom.</p>
        <div className="flex gap-2 mt-2">
          <Button variant="outline" className="flex-1 rounded-full" onClick={onCancel} data-testid="cropper-cancel">Cancel</Button>
          <Button className="flex-1 bg-turquoise text-navy font-bold rounded-full" onClick={confirm} disabled={busy || !pixels} data-testid="cropper-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Use Photo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
